/**
 * Screen layout: working out where everything goes on screen, in pixels.
 *
 * The game's rules think in grid cells and track slots, not pixels. This file
 * converts between the two. `computeLayout` picks a cell size (in pixels) that
 * fits the whole board in the window, and every other measurement is a
 * multiple of that cell size. That way the board scales smoothly to any window.
 *
 * Both the renderer (to draw things) and the click handler (to work out what
 * was clicked, called "hit testing") use this same layout. Sharing one source
 * of truth means what you see and what you can click can never drift apart.
 */
import type { GameState } from "./state";
import { slotCoords, trackLength, trackPoint } from "./track";

/**
 * Where everything is on screen for the current window size. All values are
 * in pixels unless noted.
 */
export interface Layout {
	cell: number;
	/**
	 * The grid's size in cells, and the gap between the grid and the track (in
	 * cells). Kept here so the conversion functions below don't also need the
	 * game state.
	 */
	gridWidth: number;
	gridHeight: number;
	trackGap: number;
	/**
	 * Size of the blocks in the tray and columns, and the distance between
	 * stacked blocks, in pixels. These are the click and tap targets, so they
	 * never go below `LayoutSettings.minTarget` unless the screen is too narrow.
	 */
	blockSize: number;
	blockPitch: number;
	/** Size of blocks on the track, in pixels: in proportion to the track's width. */
	trackBlockSize: number;
	/**
	 * How thick the track is, in pixels: one grid cell, or `minTrack` if that's
	 * bigger, so the track and the blocks on it stay visible on big grids.
	 */
	trackWidth: number;
	/** How many blocks of each column are drawn; deeper ones are shown as a "+N" count. */
	shownDepth: number;
	/** Pixel position of the top-left corner of the grid's top-left cell. */
	gridLeft: number;
	gridTop: number;
	/** Where to center the level title, in the margin above the board. */
	title: { x: number; y: number };
	/** Where the "3/5" track counter starts (its left edge, vertically centered), just below the track's bottom-left corner. */
	trackCounter: { x: number; y: number };
	/** Pixel y of the top edge of the tray slots. */
	trayTop: number;
	/** Pixel x of the center of each tray slot. */
	trayCenters: number[];
	/** Pixel y of the top edge of the front block in each column. */
	columnsTop: number;
	/** Pixel x of the center of each column. */
	columnCenters: number[];
}

/**
 * Sizes and spacing that control the look of the layout. All are measured in
 * grid cells, so a value of 0.5 means half a cell, whatever size that turns
 * out to be in pixels.
 */
export interface LayoutSettings {
	/** How big a block is. */
	blockSize: number;
	/** Distance from one block to the next in a column (block size plus a small gap). */
	blockPitch: number;
	/** Distance between the centers of neighbouring columns. */
	columnPitch: number;
	/** Distance between the centers of neighbouring tray slots. */
	trayPitch: number;
	/** Empty space between the grid and the track. */
	trackGap: number;
	/** Space between the track and the tray, and between the tray and the columns. */
	sectionGap: number;
	/** Margin around the whole board. */
	padding: number;
	/**
	 * How many blocks of each column to show. Big levels can have dozens of
	 * blocks per column; showing them all would shrink the whole board, so the
	 * rest are summarised as a "+N" count under the column.
	 */
	visibleDepth: number;
	/**
	 * The smallest size, in CSS pixels, for tray slots and column blocks, the
	 * things players click or tap. On big grids the grid shrinks to make room,
	 * rather than these. 44 is the size accessibility guidelines recommend for
	 * touch targets (WCAG 2.5.5). Unlike the other settings, this is in pixels.
	 */
	minTarget: number;
	/**
	 * The thinnest the track can be, in CSS pixels. Normally the track is one
	 * grid cell thick; on big grids, cells are tiny, so it's kept at least
	 * this thick (and the grid shrinks a little to make room). The blocks on
	 * the track are sized from its thickness.
	 */
	minTrack: number;
	/**
	 * Space kept clear at the top, in CSS pixels, for the level title and the
	 * toolbar buttons (mute, theme, help) in the top-left corner. On narrow
	 * screens the title goes below the toolbar, so a little more is kept. 0 for
	 * none (the how-to page's demo boards have no title or toolbar).
	 */
	header: number;
	/**
	 * Allow putting the tray and columns beside the grid, rather than below it,
	 * when that gives the grid more room (wide screens). The how-to page's demo
	 * boards turn this off, since their labels expect the stacked layout.
	 */
	sideBySide: boolean;
}

/** The default sizes and spacing. */
export const DEFAULT_LAYOUT: LayoutSettings = {
	blockSize: 0.8,
	blockPitch: 1.05,
	columnPitch: 1.3,
	trayPitch: 1.1,
	trackGap: 0.4,
	sectionGap: 0.75,
	padding: 0.75,
	visibleDepth: 3,
	minTarget: 44,
	minTrack: 32,
	header: 60,
	sideBySide: true,
};

/**
 * Work out the layout for a window of `viewWidth` x `viewHeight` pixels.
 *
 * First it adds up how many cells wide and tall the whole board is (grid,
 * track, tray, columns, gaps). The cell size is then the largest that fits
 * in both directions, and everything is centered.
 */
export function computeLayout(
	state: GameState,
	viewWidth: number,
	viewHeight: number,
	settings: LayoutSettings = DEFAULT_LAYOUT,
): Layout {
	const {
		blockSize,
		blockPitch,
		columnPitch,
		trayPitch,
		trackGap,
		sectionGap,
		padding,
		visibleDepth,
		minTarget,
		header: headerSetting,
		sideBySide,
		minTrack,
	} = settings;
	/** The track plus the gap inside it, on each side of the grid, in cells (when the track is one cell thick). */
	const ring = 1 + trackGap;
	/** The track and the gap inside it, on each side of the grid, in pixels. */
	const ringPx = (cell: number) =>
		trackThickness(cell, minTrack) + trackGap * cell;
	// On narrow screens (phones), the centered title would run into the
	// toolbar, so it goes underneath it instead, which needs more room.
	const narrow = viewWidth < 560;
	const header =
		headerSetting > 0 && narrow ? headerSetting + 28 : headerSetting;
	/** The height left for the board itself, below the header. */
	const usable = viewHeight - header;
	const { width, height } = state.grid;
	const columnCount = state.columns.length;
	const traySize = state.tray.length;

	// The board has two parts, each measured in its own units:
	// - the top: the grid and the track around it, measured in grid cells (`cell`);
	// - the bottom: the tray and the columns, measured in `unit`s, where a
	//   block is `blockSize` units across.
	// Usually the two units are the same size. On big grids, cells get small,
	// but the bottom's blocks are what players click, so they're kept at
	// least `minTarget` pixels: `unit` grows, and the grid shrinks to make room.
	const topWide = width + ring * 2 + padding * 2;
	const topHigh = height + ring * 2 + padding;
	const bottomWide =
		Math.max(columnCount * columnPitch, traySize * trayPitch) + padding * 2;
	/** Height of the bottom part, in units, showing `depth` blocks of each column. */
	const bottomHigh = (depth: number) =>
		sectionGap +
		blockSize +
		sectionGap +
		depth * blockPitch +
		(state.columnDepth > depth ? 0.6 : 0) +
		padding;

	let shownDepth = Math.min(state.columnDepth, Math.max(1, visibleDepth));
	// First try one size for everything, as on small grids.
	let cell = Math.min(
		viewWidth / Math.max(topWide, bottomWide),
		usable / (topHigh + bottomHigh(shownDepth)),
	);

	// The bottom part, in pixels: block size, the distance between stacked
	// blocks, gaps, and the distance between neighbouring columns and tray slots.
	let blockPx = blockSize * cell;
	let pitchPx = blockPitch * cell;
	let gapPx = sectionGap * cell;
	let overflowPx = state.columnDepth > shownDepth ? 0.6 * cell : 0;
	let bottomPadPx = padding * cell;
	let columnSpacingPx = columnPitch * cell;
	let traySpacingPx = trayPitch * cell;

	if (blockPx < minTarget) {
		// Big grid: cells are too small for blocks players have to click. The
		// grid should still get as much room as possible, so the bottom part is
		// packed tightly, in fixed pixel sizes: the tray row and the visible
		// column rows at the minimum block size, with small gaps between them.
		blockPx = minTarget;
		// If a row of blocks is too wide for the screen (phones), close up the
		// gaps between them, down to a tenth of a block; only if that still
		// doesn't fit do the blocks get smaller.
		const margin = 16;
		const spacing = (count: number, pitch: number) =>
			count === 0
				? 0
				: Math.max(
						blockPx * 1.1,
						Math.min(
							(pitch / blockSize) * blockPx,
							(viewWidth - margin) / count,
						),
					);
		columnSpacingPx = spacing(columnCount, columnPitch);
		traySpacingPx = spacing(traySize, trayPitch);
		const rowWide = Math.max(
			columnCount * columnSpacingPx,
			traySize * traySpacingPx,
		);
		if (rowWide > viewWidth - margin) {
			const shrink = (viewWidth - margin) / rowWide;
			blockPx *= shrink;
			columnSpacingPx *= shrink;
			traySpacingPx *= shrink;
		}
		pitchPx = blockPx + 8;
		gapPx = 10;
		bottomPadPx = 8;
		const bottomPx = (depth: number) =>
			gapPx +
			blockPx +
			gapPx +
			depth * pitchPx +
			(state.columnDepth > depth ? 22 : 0) +
			bottomPadPx;
		// Only on very short screens (a phone held sideways, say), show fewer
		// blocks per column, so the grid keeps at least a quarter of the height.
		while (shownDepth > 1 && bottomPx(shownDepth) > usable * 0.75) shownDepth--;
		overflowPx = state.columnDepth > shownDepth ? 22 : 0;
		cell = fitCell(
			{ cells: width + padding * 2, pixels: viewWidth },
			{ cells: height + padding, pixels: usable - bottomPx(shownDepth) },
			trackGap,
			minTrack,
		);
	}

	const bottomTotal =
		gapPx + blockPx + gapPx + shownDepth * pitchPx + overflowPx + bottomPadPx;
	const total = (height + padding) * cell + 2 * ringPx(cell) + bottomTotal;
	const boardTop = header + (usable - total) / 2 + padding * cell;
	const gridLeft = (viewWidth - width * cell) / 2;
	const gridTop = boardTop + ringPx(cell);
	const trackBottom = gridTop + height * cell + ringPx(cell);
	const trayTop = trackBottom + gapPx;
	const columnsTop = trayTop + blockPx + gapPx;

	// On wide screens, putting the tray and columns beside the grid instead
	// can give the grid much more room. Work that arrangement out too, and use
	// it if the grid comes out clearly bigger.
	if (sideBySide) {
		const side = besideGrid({
			state,
			viewWidth,
			usable,
			header,
			trackGap,
			minTrack,
			blockSize,
			columnPitch,
			trayPitch,
			minBlockPx: blockPx,
			shownDepth,
		});
		if (side && side.cell > cell * 1.1) {
			return {
				...side,
				gridWidth: width,
				gridHeight: height,
				trackGap,
				trackBlockSize: blockSize * trackThickness(side.cell, minTrack),
				trackWidth: trackThickness(side.cell, minTrack),
				title: {
					x: viewWidth / 2,
					y:
						header === 0
							? side.gridTop / 2
							: narrow
								? header - 18
								: header / 2 + 2,
				},
			};
		}
	}

	return {
		cell,
		gridWidth: width,
		gridHeight: height,
		trackGap,
		blockSize: blockPx,
		blockPitch: pitchPx,
		trackBlockSize: blockSize * trackThickness(cell, minTrack),
		trackWidth: trackThickness(cell, minTrack),
		shownDepth,
		gridLeft,
		gridTop,
		// Level with the toolbar's middle (it's 40px tall, 12px from the top),
		// or just below the toolbar on narrow screens.
		title: {
			x: viewWidth / 2,
			y:
				header === 0
					? boardTop - (padding / 2) * cell
					: narrow
						? header - 18
						: header / 2 + 2,
		},
		trackCounter: {
			x: gridLeft - ringPx(cell),
			y: trackBottom + gapPx / 2,
		},
		trayTop,
		trayCenters: centeredRow(viewWidth, traySize, traySpacingPx),
		columnsTop,
		columnCenters: centeredRow(viewWidth, columnCount, columnSpacingPx),
	};
}

/** What `besideGrid` works out: the parts of a `Layout` that differ between arrangements. */
type SideLayout = Pick<
	Layout,
	| "cell"
	| "blockSize"
	| "blockPitch"
	| "shownDepth"
	| "gridLeft"
	| "gridTop"
	| "trackCounter"
	| "trayTop"
	| "trayCenters"
	| "columnsTop"
	| "columnCenters"
>;

/**
 * The side-by-side arrangement: grid and track on the left, filling the
 * height, with the tray and columns in a block to their right, vertically
 * centered. Returns null if it can't fit.
 */
function besideGrid(input: {
	state: GameState;
	viewWidth: number;
	usable: number;
	header: number;
	trackGap: number;
	minTrack: number;
	blockSize: number;
	columnPitch: number;
	trayPitch: number;
	/** The smallest block size to use, in pixels. */
	minBlockPx: number;
	shownDepth: number;
}): SideLayout | null {
	const {
		state,
		viewWidth,
		usable,
		header,
		trackGap,
		minTrack,
		blockSize,
		columnPitch,
		trayPitch,
		minBlockPx,
		shownDepth,
	} = input;
	/** The arrangement with blocks of `blockPx` pixels. */
	const arrange = (blockPx: number): SideLayout | null => {
		const { width, height } = state.grid;
		const columnCount = state.columns.length;
		const traySize = state.tray.length;
		const columnSpacing = (columnPitch / blockSize) * blockPx;
		const traySpacing = (trayPitch / blockSize) * blockPx;
		const gap = 10;
		const pitch = blockPx + 8;
		const overflow = state.columnDepth > shownDepth ? 22 : 0;
		const panelWide = Math.max(
			columnCount * columnSpacing,
			traySize * traySpacing,
		);
		const panelHigh = blockPx + gap + shownDepth * pitch + overflow;
		const margin = 16;
		const between = Math.max(24, blockPx * 0.6);
		/** Room under the track for the "3/5" counter. */
		const counterRoom = 28;
		if (panelHigh > usable) return null;

		const cell = fitCell(
			{ cells: width, pixels: viewWidth - panelWide - between - margin * 2 },
			{ cells: height, pixels: usable - counterRoom - margin },
			trackGap,
			minTrack,
		);
		if (cell <= 0) return null;

		const ringPx = trackThickness(cell, minTrack) + trackGap * cell;
		const boardWide = width * cell + 2 * ringPx;
		const boardHigh = height * cell + 2 * ringPx;
		const left = (viewWidth - (boardWide + between + panelWide)) / 2;
		const boardTop = header + (usable - counterRoom - boardHigh) / 2;
		const gridLeft = left + ringPx;
		const gridTop = boardTop + ringPx;
		const panelLeft = left + boardWide + between;
		const trayTop = header + (usable - panelHigh) / 2;
		return {
			cell,
			blockSize: blockPx,
			blockPitch: pitch,
			shownDepth,
			gridLeft,
			gridTop,
			trackCounter: {
				x: gridLeft - ringPx,
				y: boardTop + boardHigh + counterRoom / 2,
			},
			trayTop,
			trayCenters: rowCenters(panelLeft, panelWide, traySize, traySpacing),
			columnsTop: trayTop + blockPx + gap,
			columnCenters: rowCenters(
				panelLeft,
				panelWide,
				columnCount,
				columnSpacing,
			),
		};
	};
	// Blocks grow with the grid (up to 72px) so they don't look tiny next to a
	// small grid's big cells; the grid is then fitted again around them.
	const first = arrange(minBlockPx);
	if (!first) return null;
	const grown = Math.min(72, Math.max(minBlockPx, blockSize * first.cell));
	return grown > minBlockPx ? (arrange(grown) ?? first) : first;
}

/** The track's thickness in pixels for a given cell size: one cell, but at least `minTrack`. */
function trackThickness(cell: number, minTrack: number): number {
	return Math.max(cell, minTrack);
}

/**
 * The largest cell size at which a grid of `across.cells` x `down.cells`,
 * plus the track and its gap on every side, fits in `across.pixels` x
 * `down.pixels`. The track is one cell thick, unless that's thinner than
 * `minTrack` pixels, in which case it's `minTrack` thick.
 */
function fitCell(
	across: { cells: number; pixels: number },
	down: { cells: number; pixels: number },
	trackGap: number,
	minTrack: number,
): number {
	// Try a track one cell thick first.
	const ring = 1 + trackGap;
	const cell = Math.min(
		across.pixels / (across.cells + 2 * ring),
		down.pixels / (down.cells + 2 * ring),
	);
	if (cell >= minTrack) return cell;
	// Too thin: the track takes a fixed minTrack on each side, the grid the rest.
	return Math.max(
		0,
		Math.min(
			(across.pixels - 2 * minTrack) / (across.cells + 2 * trackGap),
			(down.pixels - 2 * minTrack) / (down.cells + 2 * trackGap),
		),
	);
}

/** The x centers of `count` items spaced `pitch` apart, centered within `width` starting at `left`. */
function rowCenters(
	left: number,
	width: number,
	count: number,
	pitch: number,
): number[] {
	const start = left + (width - count * pitch) / 2;
	return Array.from({ length: count }, (_, i) => start + (i + 0.5) * pitch);
}

/** The x position of the center of each of `count` items spaced `pitch` apart, centered across the view. */
function centeredRow(
	viewWidth: number,
	count: number,
	pitch: number,
): number[] {
	const left = (viewWidth - count * pitch) / 2;
	return Array.from({ length: count }, (_, i) => left + (i + 0.5) * pitch);
}

/**
 * Convert a position in grid coordinates (cells, where (0, 0) is the
 * top-left cell) to the pixel at its center.
 *
 * Positions just outside the grid, such as the track at -1 or `width`, are
 * placed on the middle of the track, which is past the gap and can be thicker
 * than a cell (see `trackWidth`). The step out from the edge cell happens
 * gradually rather than jumping, so a block moving onto the track glides
 * smoothly.
 */
export function gridToPixel(
	layout: Layout,
	x: number,
	y: number,
): { x: number; y: number } {
	const {
		cell,
		gridLeft,
		gridTop,
		gridWidth,
		gridHeight,
		trackGap,
		trackWidth,
	} = layout;
	// From the middle of an edge cell to the middle of the track beside it.
	const out = 0.5 * cell + trackGap * cell + trackWidth / 2;
	const along = (v: number, size: number, start: number) => {
		const first = start + 0.5 * cell;
		const last = start + (size - 0.5) * cell;
		if (v < 0) return first + Math.max(v, -1) * out;
		if (v > size - 1) return last + Math.min(v - (size - 1), 1) * out;
		return first + v * cell;
	};
	return { x: along(x, gridWidth, gridLeft), y: along(y, gridHeight, gridTop) };
}

/**
 * Which box in a row of block-sized boxes contains the point (`px`, `py`),
 * if any. `centers` are the boxes' x centers and `top` their top edge.
 */
function hitRow(
	layout: Layout,
	centers: number[],
	top: number,
	px: number,
	py: number,
): number | null {
	const size = layout.blockSize;
	if (py < top || py > top + size) return null;
	const index = centers.findIndex((cx) => Math.abs(px - cx) <= size / 2);
	return index === -1 ? null : index;
}

/** Which column's front block is at this pixel, if any (used to handle clicks). */
export function columnAt(
	layout: Layout,
	px: number,
	py: number,
): number | null {
	return hitRow(layout, layout.columnCenters, layout.columnsTop, px, py);
}

/** Which tray slot is at this pixel, if any (used to handle clicks). */
export function traySlotAt(
	layout: Layout,
	px: number,
	py: number,
): number | null {
	return hitRow(layout, layout.trayCenters, layout.trayTop, px, py);
}

/** The slot numbers of the track's four corners, starting with the bottom-left start corner. */
export function cornerSlots(width: number, height: number): number[] {
	return [0, width + 1, width + height + 2, 2 * width + height + 3];
}

/** Pixel position of a track slot (whole slot numbers; see `trackToPixel` for in-between positions). */
export function slotToPixel(
	layout: Layout,
	slot: number,
): { x: number; y: number } {
	const length = trackLength(layout.gridWidth, layout.gridHeight);
	const { x, y } = slotCoords(
		((slot % length) + length) % length,
		layout.gridWidth,
		layout.gridHeight,
	);
	return gridToPixel(layout, x, y);
}

/**
 * Pixel position for any track position, including in-between ones like 3.5.
 *
 * Along the straight sides, this just follows the slots. At a rounded
 * corner, from the slot before the corner to the slot after it, the path
 * follows a quarter circle instead, curving smoothly onto the next side.
 * The start corner is only rounded when the track is a closed loop.
 */
export function trackToPixel(
	layout: Layout,
	position: number,
	closed: boolean,
): { x: number; y: number } {
	const { gridWidth: width, gridHeight: height } = layout;
	const length = trackLength(width, height);
	const p = ((position % length) + length) % length;
	for (const corner of cornerSlots(width, height)) {
		if (corner === 0 && !closed) continue;
		// How far past the slot before this corner the position is. The `% length`
		// arithmetic handles wrapping around from the end of the track to the start.
		const u = (((p - (corner - 1)) % length) + length) % length;
		if (u > 2) continue;
		const before = slotToPixel(layout, corner - 1);
		const at = slotToPixel(layout, corner);
		const after = slotToPixel(layout, corner + 1);
		// The two straight sides and the corner form three corners of a square; the
		// arc's center is the fourth corner.
		const cx = before.x + after.x - at.x;
		const cy = before.y + after.y - at.y;
		const radius = Math.hypot(before.x - cx, before.y - cy);
		const start = Math.atan2(before.y - cy, before.x - cx);
		let sweep = Math.atan2(after.y - cy, after.x - cx) - start;
		if (sweep > Math.PI) sweep -= 2 * Math.PI;
		if (sweep < -Math.PI) sweep += 2 * Math.PI;
		const angle = start + (sweep * u) / 2;
		return {
			x: cx + Math.cos(angle) * radius,
			y: cy + Math.sin(angle) * radius,
		};
	}
	const point = trackPoint(p, width, height);
	return gridToPixel(layout, point.x, point.y);
}
